import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity, EntityRefLinks, getEntityRelations } from '@backstage/plugin-catalog-react';
import {
  Avatar, EmailIcon, InfoCard, Link, Progress, StructuredMetadataTable,
} from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, List, ListItem, ListItemIcon, ListItemText, Tooltip,
} from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import { OktaUserEntity, RELATION_MANAGED_BY, RELATION_MANAGER_OF } from '@eng-portal/czi-extensions-common';
import { RELATION_MEMBER_OF, stringifyEntityRef, Entity } from '@backstage/catalog-model';
import { omit } from 'lodash';
import { CardTitle } from './CardTitle';
import useAsync from 'react-use/lib/useAsync';

export function OktaUserProfileCard(): JSX.Element {
  const { entity: user } = useEntity<OktaUserEntity>();
  const catalogApi = useApi(catalogApiRef);

  const managedByRelations = getEntityRelations(user, RELATION_MANAGED_BY, {
    kind: 'User',
  });
  const managerRef = managedByRelations.length > 0
    ? stringifyEntityRef(managedByRelations[0])
    : undefined;

  const { value: peersAndReports, loading: peersLoading } = useAsync(async () => {
    try {
      const currentUserRef = stringifyEntityRef(user);

      let manager: Entity | undefined;
      const peers: Entity[] = [];
      if (managerRef) {
        const [managerResult, peersResponse] = await Promise.all([
          catalogApi.getEntitiesByRefs({ entityRefs: [managerRef] }),
          catalogApi.getEntities({
            filter: {
              kind: 'User',
              [`relations.${RELATION_MANAGED_BY}`]: managerRef,
            },
          }),
        ]);
        manager = managerResult.items[0] ?? undefined;
        for (const entity of peersResponse.items) {
          if (stringifyEntityRef(entity) !== currentUserRef) {
            peers.push(entity);
          }
        }
      }

      const reportRelations = getEntityRelations(user, RELATION_MANAGER_OF, {
        kind: 'User',
      });
      const { items: reportItems } = await catalogApi.getEntitiesByRefs({
        entityRefs: reportRelations.map(stringifyEntityRef),
      });
      const directReports = reportItems.filter((e): e is Entity => e !== undefined);

      return { manager, peers, directReports };
    } catch {
      return { manager: undefined, peers: [] as Entity[], directReports: [] as Entity[] };
    }
  }, [managerRef, catalogApi, user]);

  if (!user) {
    return <Alert severity="error">User not found</Alert>;
  }

  const {
    metadata: { description },
    spec: { profile },
  } = user;
  const { displayName, email } = profile;
  const emailHref = email ? `mailto:${email}` : undefined;

  const memberOfRelations = getEntityRelations(user, RELATION_MEMBER_OF, {
    kind: 'Group',
  });

  const peers = peersAndReports?.peers ?? [];
  const directReports = peersAndReports?.directReports ?? [];

  return (
    <InfoCard
      title={<CardTitle title={displayName} />}
      subheader={description}
    >
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} sm={2} xl={1}>
          <Avatar
            displayName={displayName}
            picture={profile?.picture}
          />
        </Grid>
        <Grid item md={10} xl={11}>
          <List>
            {profile?.email && (
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Email">
                    <EmailIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText>
                  <Link to={emailHref ?? ''}>{profile.email}</Link>
                </ListItemText>
              </ListItem>
            )}
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Hierarchy">
                  <GroupIcon />
                </Tooltip>
              </ListItemIcon>
              <StructuredMetadataTable metadata={{
                manager: peersLoading ? (
                  <Progress />
                ) : peersAndReports?.manager ? (
                  <EntityRefLinks
                    entityRefs={[peersAndReports.manager]}
                    defaultKind="User"
                  />
                ) : (profile?.manager || undefined),
                'peers (same manager)': peersLoading ? (
                  <Progress />
                ) : peers.length > 0 ? (
                  <EntityRefLinks entityRefs={peers} defaultKind="User" />
                ) : (
                  <span style={{ color: '#888' }}>
                    {managerRef ? 'None found' : 'No manager relation set'}
                  </span>
                ),
                'direct reports': peersLoading ? (
                  <Progress />
                ) : directReports.length > 0 ? (
                  <EntityRefLinks entityRefs={directReports} defaultKind="User" />
                ) : (
                  <span style={{ color: '#888' }}>None</span>
                ),
                ...omit(profile, ['displayName', 'email', 'picture', 'manager', 'managerId', 'managerRef']),
              }} />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Member of">
                  <GroupIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText>
                <EntityRefLinks
                  entityRefs={memberOfRelations}
                  defaultKind="Group"
                />
              </ListItemText>
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </InfoCard>
  );
}
